import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Commands, Downloads, Results, Search, Style } from '@local/client-contracts';
import { UiIconModel } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuService } from '@shared/components';
import { EventInfo, EventsService } from '@shared/services';
import { DownloadsService } from '@shared/services/downloads.service';
import { FlagsService } from '@shared/services/flags.service';
import { CustomKeyboardEvent } from '@shared/services/keyboard.service';
import { ResourcesService } from '@shared/services/resources.service';
import { isDownloadUrlCommand, isOpenUrlCommand } from '@shared/utils';
import { getDownloadIcon } from '@shared/utils/set-icon.util';
import { Logger } from '@unleash-tech/js-logger';
import moment from 'moment';
import { Subject } from 'rxjs';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { ResultCommandService } from 'src/app/bar/services/commands/result-command.service';
import { ContextMenuInvokeSource } from 'src/app/bar/services/commands/results-item-context-menu-helper';
import { ResultsResourceItemContextMenuHelper } from 'src/app/bar/services/commands/results-resource-item-context-menu-helper';
import { HubService } from 'src/app/bar/services/hub.service';
import { PeopleCommandBuilder } from 'src/app/bar/views/results-views/people/people-command-builder.service';
import { InvokeCommand } from 'src/app/bar/views/results/models/invoke-command.model';
import { ResultContextMenuItem, SearchResults, TelemetryTrigger } from 'src/app/bar/views/results/models/results-types';
import { ResultCommandBuilder } from 'src/app/bar/views/results/services/result-command-builder.service';
import { getPrimaryActionClick, isPreviewClickAction, isWikiCard } from 'src/app/bar/views/results/utils/results.util';
import { GlobalErrorHandler } from 'src/app/global-error-handler';
import { PreviewMode } from '../../../../model/preview-mode';
import { PeopleService } from '../../../services/people.service';
import { WorkspacesService } from 'src/app/bar/services';
import { Constants } from '@local/common';

export type PreviewResultItem = { model: Search.ResultResourceItem; userEmail: string; userName: string };
type SubtitleView = { title: string; name?: string; time: string };
@UntilDestroy()
@Component({
  selector: 'preview-result-item',
  templateUrl: './preview-result-item.component.html',
  styleUrls: ['./preview-result-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewResultItemComponent implements OnDestroy, OnInit {
  restrictedLink: Boolean;
  subTitleView: SubtitleView;
  DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG = Constants.DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG;

  private _item: PreviewResultItem;
  public get item(): PreviewResultItem {
    return this._item;
  }
  @Input() public set item(value: PreviewResultItem) {
    this._item = value;
    this.subTitleView = this.generateSubTitle();
    if (this.subTitleView) {
      this.subTitleView.time = moment(this.subTitleView.time).fromNow();
      this.subTitleView.name = this.item.userName;
    }
    this.restrictedLink = this.item?.model?.link?.shareOptions?.restricted;
  }
  @Input() size: PreviewMode;
  @Input() index: number;
  @Input() selected: boolean;
  @Input() telemetrySearch: Partial<EventInfo>;
  @Input() smallWidthPreview = true;
  @Input() previewPopupLarge: boolean;
  @Input() showResultSections: Results.ShowResultSections = {
    showContextMenu: true,
  };
  contextMenuHelper: ResultsResourceItemContextMenuHelper;
  private logger: Logger;
  @ViewChild('subtitle', { read: ElementRef }) subtitleRef: ElementRef;
  @ViewChild('title', { read: ElementRef }) titleRef: ElementRef;
  @ViewChild('actions', { read: ElementRef }) actionsRef: ElementRef;

  @HostListener('click', ['$event'])
  openUrl(event: CustomKeyboardEvent, trigger: TelemetryTrigger = 'mouse_click') {
    event.stopPropagation();
    const searchId = this.telemetrySearch?.search?.searchId;
    const item = this.item.model as SearchResults;
    if (isPreviewClickAction(item.action)) {
      this.onPreviewClick(item);
    } else {
      this.resultCommandService.executeCommand(this.item?.model?.view?.title?.onClick, { item: this.item?.model, searchId }, 'person');
    }
  }
  contextMenuItems: Array<ResultContextMenuItem>;
  @Output() contextOpen = new EventEmitter();
  updateView$ = new Subject();

  private get eventResources() {
    return [
      {
        id: this.item?.model?.id,
        list: 'people_activities',
        appId: this.item?.model?.link?.appId,
        type: this.item?.model?.resource?.type,
        position: this.index,
        linkId: this.item?.model?.link?.id,
      },
    ];
  }

  constructor(
    private resultCommandBuilder: ResultCommandBuilder,
    private hostRef: ElementRef,
    private contextMenuService: ContextMenuService,
    private errorHandler: GlobalErrorHandler,
    private cdr: ChangeDetectorRef,
    private peopleService: PeopleService,
    private eventService: EventsService,
    private peopleBuilder: PeopleCommandBuilder,
    private resourcesService: ResourcesService,
    private flagsService: FlagsService,
    private resultCommandService: ResultCommandService,
    private hubService: HubService,
    private commandsService: CommandsService,
    protected downloadsService: DownloadsService,
    private workspacesService: WorkspacesService
  ) {}

  ngOnInit(): void {
    this.updateView$.pipe(untilDestroyed(this)).subscribe(() => this.cdr.markForCheck());
  }

  ngAfterViewInit(): void {
    this.contextMenuHelper = new ResultsResourceItemContextMenuHelper(
      this.resultCommandBuilder,
      this.logger,
      this.contextMenuService,
      this.hostRef,
      this.titleRef,
      this.subtitleRef,
      this.actionsRef,
      this.errorHandler,
      this.resourcesService,
      this.hubService,
      this.flagsService,
      this.workspacesService
    );
    this.contextMenuHelper.invoke$.pipe(untilDestroyed(this)).subscribe(({ item, trigger }) => this.emitContextMenuCommand(item, trigger));

    this.contextMenuHelper.buildItemsRequest(null, this.item.model, 'actions').then((item) => {
      this.peopleBuilder.build(item).then((res) => (this.contextMenuItems = res));
    });
  }

  ngOnDestroy(): void {
    if (this.contextMenuHelper.ref) this.contextMenuHelper.ref.destroy();
  }

  private emitContextMenuCommand(item: ResultContextMenuItem, trigger: TelemetryTrigger): void {
    const label = item.id;
    const searchId = this.telemetrySearch?.search?.searchId;
    const ev: Partial<EventInfo> = {
      label,
      location: { title: this.peopleService.currentLocation },
      resources: this.eventResources,
    };
    this.eventService.event('context_menu.open_from_preview', ev);
    if (label === 'favorite') {
      this.item.model.isFavorite = !this.item.model.isFavorite;
      this.resultCommandService.toggleFavorite(this.item.model, this.eventResources, trigger, searchId);
      this.cdr.markForCheck();
      return;
    }
    if (item.command.type === 'download-url') {
      this.download({ command: item.command, context: item.data, trigger });
      return;
    }
    if (
      isOpenUrlCommand(item.command) &&
      getPrimaryActionClick((this.item.model as SearchResults)?.action) === 'preview' &&
      isWikiCard(this.item.model)
    ) {
      item.command = this.buildPreviewCommand();
    }
    this.resultCommandService.executeCommand(item.command, { item: this.item?.model, searchId }, item.command.type as Search.ResultType, {
      target: trigger,
      location: { title: this.peopleService.currentLocation },
      label,
      resources: this.eventResources,
    });
  }

  buildPreviewCommand(): Commands.Preview<SearchResults> {
    return {
      type: 'preview',
      previewable: true,
      state: 'popup',
      model: this.item.model,
      selectedIndex: this.index,
    };
  }

  private download(invokeData?: InvokeCommand) {
    const dragCommand: Commands.DragCommand = (this.item.model.view.title as Results.Title).onDrag;
    if (!isDownloadUrlCommand(dragCommand)) return;
    const icon = getDownloadIcon(this.item.model.view.icon, this.item.model.view.iconOverlay);

    this.eventService.event('context_menu.download', {
      name: 'action',
      location: { title: this.peopleService.currentLocation },
      resources: this.eventResources,
    });

    const req: Downloads.ResourceDownloadRequest = {
      type: 'Resource',
      url: dragCommand.url,
      resource: this.item.model.resource,
      icon,
    };
    this.downloadsService.resourceDownload(req);
  }

  async openContextMenu(event: MouseEvent, source: ContextMenuInvokeSource) {
    event.stopPropagation();
    event.preventDefault();
    this.eventService.event('context_menu.open_from_preview', {
      label: 'open_menu',
      location: { title: this.peopleService.currentLocation },
      resources: this.eventResources,
    });
    const ref = await this.contextMenuHelper.open(event, this.item?.model, source);
    ref.destroy$.pipe(untilDestroyed(this)).subscribe(() => this.cdr.markForCheck());
    this.contextOpen.emit();
  }

  private generateSubTitle(): SubtitleView {
    const traits = this.item?.model?.resource?.traits;
    let subtitle: SubtitleView = null;
    if (traits?.reporter?.email === this.item.userEmail && this.compareDate(subtitle, traits?.createdAt)) {
      subtitle = { title: 'Opened it', time: traits?.createdAt };
    }
    if (
      traits?.assignees &&
      traits.assignees.some((as) => as?.email === this.item.userEmail) &&
      this.compareDate(subtitle, traits?.modifiedAt || traits?.createdAt)
    ) {
      subtitle = { title: 'Assigned to', time: traits?.modifiedAt || traits?.createdAt };
    }
    if (traits?.createdBy?.email === this.item.userEmail && this.compareDate(subtitle, traits?.createdAt)) {
      subtitle = { title: 'Created by', time: traits?.createdAt };
    }
    if (traits?.modifiedBy?.email === this.item.userEmail && this.compareDate(subtitle, traits?.modifiedAt || traits?.createdAt)) {
      subtitle = { title: 'Modified by', time: traits?.modifiedAt || traits?.createdAt };
    }
    if (traits?.sender?.email === this.item.userEmail && this.compareDate(subtitle, traits?.createdAt)) {
      subtitle = { title: 'Sent by', time: traits?.createdAt };
    }
    if (
      traits?.recipients &&
      traits?.recipients.some((a) => a?.email === this.item.userEmail) &&
      this.compareDate(subtitle, traits?.createdAt)
    ) {
      subtitle = { title: 'Received it', time: traits?.createdAt };
    }
    if (traits?.sharedBy?.email === this.item.userEmail && this.compareDate(subtitle, traits?.sharedAt)) {
      subtitle = { title: 'Shared it', time: traits?.sharedAt };
    }
    return subtitle;
  }

  private compareDate(subTitle: SubtitleView, compareDate: string): boolean {
    return compareDate && (!subTitle || compareDate > subTitle.time);
  }

  generateIcon(icon: Style.Icon): UiIconModel {
    const imgIcon: UiIconModel = {
      type: 'img',
      value: {
        darkUrl: icon.darkUrl,
        lightUrl: icon.lightUrl,
      },
    };
    return imgIcon;
  }

  onPreviewClick(item: SearchResults) {
    this.commandsService.executeCommand({
      type: 'preview',
      model: item,
      previewable: true,
      state: 'popup',
      pageType: 'home',
    } as Commands.Preview<SearchResults>);
  }
}
